import { AvailableCurrencies } from 'utils/clickme';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { ActStatus, ActType } from 'store/models/acts';
import { InvoiceDreamPayerType, InvoiceStatus, PaymentType, RefundStatus } from 'store/models/invoices';

const SET_INVOICE_ID = 'SET_INVOICE_ID';
const SET_INVOICE_GENERAL_DATA = 'SET_INVOICE_GENERAL_DATA';
const SET_INVOICE_GENERAL_DATA_ACCOUNT = 'SET_INVOICE_GENERAL_DATA_ACCOUNT';
const SET_INVOICE_ACTS = 'SET_INVOICE_ACTS';
const SET_INVOICE_IS_LOAD_ACTS = 'SET_INVOICE_IS_LOAD_ACTS';
const SET_INVOICE_IS_LOAD_PAYMENTS = 'SET_INVOICE_IS_LOAD_PAYMENTS';
const SET_INVOICE_PAYMENTS = 'SET_INVOICE_PAYMENTS';
const SET_INVOICE_CHARGES = 'SET_INVOICE_CHARGES';
const SET_INVOICE_IS_LOAD_CHARGES = 'SET_INVOICE_IS_LOAD_CHARGES';
const SET_INVOICE_REFUNDS = 'SET_INVOICE_REFUNDS';

type InvoiceId = string;
type IsLoadActs = boolean;
type IsLoadPayments = boolean;
type IsLoadCharges = boolean;

export interface InvoiceDetail {
    id: number;
    amount: number;
    userId: number;
    userName: string;
    title: string;
    address: string;
    post: string;
    inn: string;
    kpp: string;
    status: InvoiceStatus;
    createdAt: string;
    updatedAt: string;
    auto: boolean;
    number: number;
    promoBonus: number;
    payerId: number;
    payerType: InvoiceDreamPayerType;
    accountId: number;
    personalManagerEmail: string;
    accountCurrency: AvailableCurrencies;
}

export interface Account {
    id: number;
    employerId: number;
}

interface Act {
    amount: number;
    type: ActType;
    accountId: number;
    emailManager: string;
    employerId: number;
    invoices: Pick<InvoiceDetail, 'id' | 'amount'>[];
    statisticAmount: number;
    actName: string;
    status: ActStatus;
    createdAt: string;
    error: string | null;
}

interface Payment {
    id: number;
    invoiceId: number;
    amount: number;
    payer: InvoiceDreamPayerType;
    createdAt: string;
    updatedAt: string;
    number: number | null;
    forCharge: number;
    paymentDate: string;
    paymentType: PaymentType;
    closed: boolean;
}

interface Charge {
    paymentIds: number[];
    date: string;
    amount: number;
    bonus: number;
}

interface User {
    id: number;
    accountId: number;
    name: string;
    hhId: number;
    email: string;
    createdAt: string;
    updatedAt: string;
    isAdmin: boolean;
    employer: number;
    hhUserId: number;
    disabled: boolean;
}

export interface Refund {
    id: number;
    paymentId: number;
    user: User;
    status: RefundStatus;
    amount: number;
    createdAt: string;
}

interface PayloadTypes {
    [SET_INVOICE_ID]: InvoiceId;
    [SET_INVOICE_GENERAL_DATA]: Partial<InvoiceDetail>;
    [SET_INVOICE_GENERAL_DATA_ACCOUNT]: Partial<Account>;
    [SET_INVOICE_ACTS]: Act[];
    [SET_INVOICE_IS_LOAD_ACTS]: IsLoadActs;
    [SET_INVOICE_IS_LOAD_PAYMENTS]: IsLoadPayments;
    [SET_INVOICE_PAYMENTS]: Payment[];
    [SET_INVOICE_CHARGES]: Charge[];
    [SET_INVOICE_IS_LOAD_CHARGES]: IsLoadCharges;
    [SET_INVOICE_REFUNDS]: Refund[];
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setInvoiceId = ActionCreator(SET_INVOICE_ID);
export const setInvoice = ActionCreator(SET_INVOICE_GENERAL_DATA);
export const setAccount = ActionCreator(SET_INVOICE_GENERAL_DATA_ACCOUNT);
export const setActs = ActionCreator(SET_INVOICE_ACTS);
export const setLoadActs = ActionCreator(SET_INVOICE_IS_LOAD_ACTS);
export const setLoadPayments = ActionCreator(SET_INVOICE_IS_LOAD_PAYMENTS);
export const setPayments = ActionCreator(SET_INVOICE_PAYMENTS);
export const setCharges = ActionCreator(SET_INVOICE_CHARGES);
export const setLoadCharges = ActionCreator(SET_INVOICE_IS_LOAD_CHARGES);
export const setRefunds = ActionCreator(SET_INVOICE_REFUNDS);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface InvoiceDetailState {
    invoiceId: InvoiceId;
    invoice: Partial<InvoiceDetail>;
    account: Partial<Account>;
    acts: Act[];
    payments: Payment[];
    charges: Charge[];
    refunds: Refund[];
    isLoadActs: IsLoadActs;
    isLoadPayments: IsLoadPayments;
    isLoadCharges: IsLoadCharges;
}

export const INVOICE_DETAIL_INITIAL_STATE: InvoiceDetailState = {
    invoiceId: '0',
    invoice: {},
    account: {},
    acts: [],
    payments: [],
    charges: [],
    refunds: [],
    isLoadActs: false,
    isLoadPayments: false,
    isLoadCharges: false,
};

export default createReducer<InvoiceDetailState, PayloadTypes>(INVOICE_DETAIL_INITIAL_STATE, {
    [SET_INVOICE_ID]: (state, { payload }) => ({
        ...state,
        invoiceId: payload,
    }),
    [SET_INVOICE_GENERAL_DATA]: (state, { payload }) => ({
        ...state,
        invoice: payload,
    }),
    [SET_INVOICE_GENERAL_DATA_ACCOUNT]: (state, { payload }) => ({
        ...state,
        account: payload,
    }),
    [SET_INVOICE_ACTS]: (state, { payload }) => ({
        ...state,
        acts: payload,
    }),
    [SET_INVOICE_IS_LOAD_ACTS]: (state, { payload }) => ({
        ...state,
        isLoadActs: payload,
    }),
    [SET_INVOICE_PAYMENTS]: (state, { payload }) => ({
        ...state,
        payments: payload,
    }),
    [SET_INVOICE_IS_LOAD_PAYMENTS]: (state, { payload }) => ({
        ...state,
        isLoadPayments: payload,
    }),
    [SET_INVOICE_CHARGES]: (state, { payload }) => ({
        ...state,
        charges: payload,
    }),
    [SET_INVOICE_IS_LOAD_CHARGES]: (state, { payload }) => ({
        ...state,
        isLoadCharges: payload,
    }),
    [SET_INVOICE_REFUNDS]: (state, { payload }) => ({
        ...state,
        refunds: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
